import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Dayjs } from 'dayjs';
import { catchError, EMPTY, Observable, tap } from 'rxjs';
import { ToastTypeEnum } from '../components/organisms/toast/toast.type';
import {
  WeightList,
  WeightListResponseDTO,
  WeightResponseDTO,
} from '../components/pages/user/weight/weight.types';
import { ApiRoutes } from '../constants/api-routes.constants';
import { RequestStateEnum } from '../enums/app.enum';
import { createEndpointUrl } from '../utilities/pure-functions';
import { ModalService } from './modal.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class WeightService {
  private weightItems: WritableSignal<WeightList> = signal([]);
  private requestState: WritableSignal<RequestStateEnum | null> = signal(null);
  private deleteRequestState: WritableSignal<RequestStateEnum | null> = signal(null);

  constructor(
    private readonly http: HttpClient,
    private readonly toastService: ToastService,
    private readonly modalService: ModalService
  ) {}

  public getRequestState(): Signal<RequestStateEnum | null> {
    return this.requestState.asReadonly();
  }

  public get getWeightItems(): Signal<WeightList | null> {
    return this.weightItems.asReadonly();
  }

  public fetchWeightItems(start: Dayjs, end: Dayjs): void {
    const url = createEndpointUrl(ApiRoutes.weights);
    const params = new HttpParams()
      .append('start', start.format('YYYY-MM-DD'))
      .append('end', end.format('YYYY-MM-DD'));

    this.http
      .get<WeightListResponseDTO>(url, { params, headers: { addAuthHeader: 'true' } })
      .subscribe({
        next: (response) => {
          this.requestState.set(RequestStateEnum.SUCCESS);
          this.weightItems.set(response.payload);
        },
        error: () => {
          this.requestState.set(RequestStateEnum.ERROR);
          this.toastService.addToast('Ladefehler', ToastTypeEnum.ERROR);
        },
      });
  }

  public createWeightItem(weight: number, date: Dayjs): void {
    this.requestState.set(RequestStateEnum.SUCCESS);
    const url = createEndpointUrl(ApiRoutes.weights);
    const body = { weight, date: date.format('YYYY-MM-DD') };

    this.http.post<WeightResponseDTO>(url, body, { headers: { addAuthHeader: 'true' } }).subscribe({
      next: (response) => {
        this.toastService.addToast('Gewichtmessung hinzugefügt.', ToastTypeEnum.SUCCESS);
        this.requestState.set(RequestStateEnum.SUCCESS);

        this.weightItems.update((arr) => [...arr, response.payload]);
        this.modalService.close();
      },
      error: () => {
        this.toastService.addToast('Fehler beim Speichern', ToastTypeEnum.ERROR);
        this.requestState.set(RequestStateEnum.ERROR);
      },
    });
  }

  public updateWeightItem(id: string, weight: number, date: Dayjs): void {
    this.requestState.set(RequestStateEnum.PENDING);

    const url = createEndpointUrl(ApiRoutes.weightById, { id });
    this.http
      .patch<WeightResponseDTO>(
        url,
        { weight, date: date.format('YYYY-MM-DD') },
        { headers: { addAuthHeader: 'true' } }
      )
      .subscribe({
        next: (response) => {
          this.toastService.addToast('Änderungen gespeichert!', ToastTypeEnum.SUCCESS);
          this.requestState.set(RequestStateEnum.SUCCESS);

          this.weightItems.update((arr) =>
            arr.map((item: any) => (item.id === id ? response.payload : item))
          );

          this.modalService.close();
        },
        error: () => {
          this.toastService.addToast('Fehler beim Speichern.', ToastTypeEnum.ERROR);
          this.requestState.set(RequestStateEnum.ERROR);
        },
      });
  }

  public deleteWeightItem(id: string): void {
    this.deleteRequestState.set(RequestStateEnum.PENDING);
    const url = createEndpointUrl(ApiRoutes.weightById, { id });

    this.http.delete<WeightResponseDTO>(url, { headers: { addAuthHeader: 'true' } }).subscribe({
      next: () => {
        this.toastService.addToast('Eintrag gelöscht!');
        this.deleteRequestState.set(RequestStateEnum.SUCCESS);
        this.weightItems.update((arr) => arr.filter((item: any) => item.id !== id));
        this.modalService.close();
      },
      error: () => {
        this.toastService.addToast('Fehler beim Löschen.');
        this.deleteRequestState.set(RequestStateEnum.ERROR);
      },
    });
  }

  public getWeightItemById(id: string): Observable<WeightResponseDTO> {
    this.requestState.set(RequestStateEnum.PENDING);
    const url = createEndpointUrl(ApiRoutes.weightById, { id });

    return this.http.get<WeightResponseDTO>(url, { headers: { addAuthHeader: 'true' } }).pipe(
      catchError(() => {
        this.requestState.set(RequestStateEnum.ERROR);
        return EMPTY;
      }),
      tap(() => this.requestState.set(RequestStateEnum.SUCCESS))
    );
  }
}
